import React from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { useParams } from 'react-router'
import { useAPI } from 'contexts/APIProvider'
import Lead from 'logic/Lead'
import AvailabilityCheck from 'logic/AvailabilityCheck'
import { NursePaygradeStats } from 'types/interfaces'
import LeadDetailContext from './context'
import LeadActionPanel from './components/LeadActionPanel'
import LeadAvailabilityChecksPanel from './components/LeadAvailabilityChecksPanel'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'

const LeadDetail: React.FC = () => {
  const { api } = useAPI()
  const { showAlert } = useSnackBarAlert()
  const params = useParams()
  const leadId = React.useMemo(() => Number(params.leadId), [params.leadId])
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'))

  const [lead, setLead] = React.useState<Lead>()
  const [availabilityCheck, setAvailabilityCheck] = React.useState<AvailabilityCheck>()
  const [loading, setLoading] = React.useState(false)
  const [paygradeStats, setPaygradeStats] = React.useState<NursePaygradeStats>()
  const [gtmCheckStats, setGtmCheckStats] = React.useState({ numTotal: 0, numNurseFound: 0, pctNurseFound: 0 })

  const fetchPaygradeStats = React.useCallback(async () => {
    try {
      setPaygradeStats(await api.getNursePaygradeStats())
    } catch (e) {
      console.error('Failed to load paygrade stats', e)
    }
  }, [api])

  React.useEffect(() => {
    fetchPaygradeStats()
  }, [fetchPaygradeStats])

  const fetchLead = React.useCallback(async () => {
    try {
      const result = await api.getLead(leadId, ['practice_stripe_ready', 'num_checks'])
      setLead(result)
    } catch (e: any) {
      showAlert('error', 'An error occurred while loading this lead.')
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [api, leadId, showAlert])

  React.useEffect(() => {
    fetchLead()
  }, [fetchLead])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!lead) return null

  return (
    <LeadDetailContext.Provider value={{ lead, availabilityCheck, paygradeStats, gtmCheckStats, setLead, setAvailabilityCheck, setGtmCheckStats }}>
      {isDesktop ? (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, p: 2 }}>
          <Box sx={{ flex: 1 }}>
            <LeadActionPanel refreshLead={fetchLead} />
          </Box>
          <Box sx={{ width: 350 }}>
            <LeadAvailabilityChecksPanel />
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', gap: 2 }}>
          <Box sx={{ flexShrink: 0 }}>
            <LeadAvailabilityChecksPanel />
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <LeadActionPanel refreshLead={fetchLead} />
          </Box>
        </Box>
      )}
    </LeadDetailContext.Provider>
  )
}

export default LeadDetail
